import { BringToFront, Copy, Lock, Pencil, SendToBack, Trash2, Unlock } from 'lucide-react';
import React, { FC, useEffect } from 'react';

import { useAdsGeneratorStore } from '../../model/store';
import { AdElement } from '../../model/types';
import { MenuItem, MenuSeparator, MenuWrapper } from './Stage.styled';

interface ElementContextMenuProps {
  element: AdElement;
  /** Pozycja kursora względem planszy, w pikselach ekranu. */
  x: number;
  y: number;
  onClose: () => void;
  onEditText: (id: string) => void;
}

/**
 * Menu podręczne elementu planszy.
 *
 * Rysujemy je jako zwykłą warstwę HTML nad płótnem, a nie kształtami Konvy:
 * menu ma wyglądać jak element interfejsu, skalować się z czcionką systemową
 * i nie trafiać do eksportowanego pliku.
 */
export const ElementContextMenu: FC<ElementContextMenuProps> = ({
  element,
  x,
  y,
  onClose,
  onEditText,
}) => {
  const duplicateElement = useAdsGeneratorStore((state) => state.duplicateElement);
  const removeElement = useAdsGeneratorStore((state) => state.removeElement);
  const moveLayer = useAdsGeneratorStore((state) => state.moveLayer);
  const updateElement = useAdsGeneratorStore((state) => state.updateElement);

  useEffect(() => {
    const close = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [onClose]);

  const run = (action: () => void) => (): void => {
    action();
    onClose();
  };

  return (
    <MenuWrapper style={{ left: x, top: y }} onContextMenu={(event) => event.preventDefault()}>
      {element.type === 'text' && (
        <MenuItem type="button" onClick={run(() => onEditText(element.id))}>
          <Pencil size={14} />
          Edytuj tekst
        </MenuItem>
      )}
      <MenuItem type="button" onClick={run(() => duplicateElement(element.id))}>
        <Copy size={14} />
        Duplikuj
      </MenuItem>
      <MenuSeparator />
      <MenuItem type="button" onClick={run(() => moveLayer(element.id, 'front'))}>
        <BringToFront size={14} />
        Na wierzch
      </MenuItem>
      <MenuItem type="button" onClick={run(() => moveLayer(element.id, 'back'))}>
        <SendToBack size={14} />
        Na spód
      </MenuItem>
      <MenuSeparator />
      <MenuItem
        type="button"
        onClick={run(() => updateElement(element.id, { locked: !element.locked }))}
      >
        {element.locked ? <Unlock size={14} /> : <Lock size={14} />}
        {element.locked ? 'Odblokuj' : 'Zablokuj'}
      </MenuItem>
      <MenuItem type="button" $danger onClick={run(() => removeElement(element.id))}>
        <Trash2 size={14} />
        Usuń
      </MenuItem>
    </MenuWrapper>
  );
};
